import React, { useEffect, useState, useCallback } from 'react';
import { 
  Container, Row, Col, Card, Button, Form, Modal, Badge, 
  Spinner, InputGroup, Table, Dropdown 
} from 'react-bootstrap';
import { 
  FaCalendarCheck, FaSearch, FaFilter, FaVideo, FaClinicMedical, 
  FaClock, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaHourglassHalf, 
  FaUser, FaPhoneAlt, FaEnvelope, FaExternalLinkAlt, FaCopy, FaEdit, 
  FaMoneyBillWave, FaNotesMedical, FaSyncAlt
} from 'react-icons/fa';
import { get, patch } from '../utils/api';
import { useToast } from './ToastContext';
import config, { CURRENCY_SYMBOL } from '../config';
import './AppointmentManagement.css';

const AppointmentManagement = () => {
  const { showToast } = useToast();
  const [appointments, setAppointments] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  // Manage Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [modalStatus, setModalStatus] = useState('');
  const [modalPaymentStatus, setModalPaymentStatus] = useState('');
  const [modalPaymentMethod, setModalPaymentMethod] = useState('');
  const [modalMeetLink, setModalMeetLink] = useState('');
  const [modalAdminNotes, setModalAdminNotes] = useState('');
  const [modalDate, setModalDate] = useState('');
  const [modalTime, setModalTime] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      let query = '/appointments?limit=100';
      if (statusFilter !== 'all') query += `&status=${statusFilter}`;
      if (typeFilter !== 'all') query += `&consultation_type=${typeFilter}`;
      if (paymentFilter !== 'all') query += `&payment_status=${paymentFilter}`;
      if (dateFilter) query += `&date=${dateFilter}`;
      if (searchQuery.trim()) query += `&search=${encodeURIComponent(searchQuery.trim())}`;

      const res = await get(query);
      if (res && res.success) {
        setAppointments(res.data || []);
        if (res.summary) setSummary(res.summary);
      }
    } catch (err) {
      showToast(err.message || 'Failed to fetch appointments', 'danger');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, paymentFilter, dateFilter, searchQuery, showToast]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleOpenManage = (appt) => {
    setSelectedAppt(appt);
    setModalStatus(appt.status || 'pending');
    setModalPaymentStatus(appt.payment_status || 'pending');
    setModalPaymentMethod(appt.payment_method || (appt.consultation_type === 'online' ? 'online' : 'pay_at_clinic'));
    setModalMeetLink(appt.google_meet_link || '');
    setModalAdminNotes(appt.admin_notes || '');
    // Format date YYYY-MM-DD
    const d = appt.appointment_date ? appt.appointment_date.slice(0, 10) : '';
    setModalDate(d);
    setModalTime(appt.appointment_time || '');
    setShowModal(true);
  };

  const handleQuickStatusChange = async (appt, newStatus) => {
    try {
      const res = await patch(`/appointments/${appt.appointment_id}/status`, { status: newStatus });
      if (res && res.success) {
        showToast(`Appointment marked as ${newStatus}!`, 'success');
        fetchAppointments();
      }
    } catch (err) {
      showToast(err.message || 'Failed to update status', 'danger');
    }
  };

  const handleQuickMarkPaid = async (appt) => {
    try {
      const res = await patch(`/appointments/${appt.appointment_id}/status`, { 
        payment_status: 'paid',
        payment_method: 'cash'
      });
      if (res && res.success) {
        showToast('Payment marked as paid at clinic!', 'success');
        fetchAppointments();
      }
    } catch (err) {
      showToast(err.message || 'Failed to update payment status', 'danger');
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (!selectedAppt) return;

    try {
      setSaving(true);
      const payload = {
        status: modalStatus,
        payment_status: modalPaymentStatus,
        payment_method: modalPaymentMethod,
        google_meet_link: modalMeetLink.trim() || null,
        appointment_date: modalDate || undefined,
        appointment_time: modalTime || undefined,
        admin_notes: modalAdminNotes.trim() || null,
      };

      const res = await patch(`/appointments/${selectedAppt.appointment_id}/status`, payload);
      if (res && res.success) {
        showToast('Appointment updated successfully!', 'success');
        setShowModal(false);
        fetchAppointments();
      }
    } catch (err) {
      showToast(err.message || 'Failed to save appointment changes', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    showToast('Google Meet link copied to clipboard!', 'info');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge bg="success"><FaCheckCircle className="me-1" /> Confirmed</Badge>;
      case 'completed':
        return <Badge bg="info"><FaCheckCircle className="me-1" /> Completed</Badge>;
      case 'cancelled':
        return <Badge bg="danger"><FaTimesCircle className="me-1" /> Cancelled</Badge>;
      case 'rescheduled':
        return <Badge bg="warning" text="dark"><FaClock className="me-1" /> Rescheduled</Badge>;
      default:
        return <Badge bg="secondary"><FaHourglassHalf className="me-1" /> Pending</Badge>;
    }
  };

  const getPaymentBadge = (pStatus, pMethod, fee) => {
    if (pStatus === 'paid') {
      return (
        <Badge bg="success" className="d-inline-flex align-items-center gap-1">
          <FaCheckCircle /> Paid ({CURRENCY_SYMBOL}{fee})
        </Badge>
      );
    }
    if (pMethod === 'pay_at_clinic') {
      return (
        <Badge bg="warning" text="dark" className="d-inline-flex align-items-center gap-1">
          <FaMoneyBillWave /> Pay at Clinic ({CURRENCY_SYMBOL}{fee})
        </Badge>
      );
    }
    return (
      <Badge bg="danger" className="d-inline-flex align-items-center gap-1">
        Pending Online ({CURRENCY_SYMBOL}{fee})
      </Badge>
    );
  };

  return (
    <Container fluid className="appointment-management-container py-4">
      {/* Header */}
      <div className="appointment-header-card mb-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div>
            <h2 className="appointment-title d-flex align-items-center gap-2">
              <FaCalendarCheck className="text-primary" /> Doctor Appointments Management
            </h2>
            <p className="appointment-subtitle mb-0">
              Track and manage patient bookings for Online Video (Google Meet) and Physical Clinic Visits.
            </p>
          </div>
          <Button variant="outline-primary" className="d-flex align-items-center gap-2" onClick={fetchAppointments}>
            <FaSyncAlt /> Refresh
          </Button>
        </div>
      </div>

      {/* Metric Summary Cards */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3} lg={2}>
          <Card className="metric-card metric-total">
            <Card.Body className="p-3">
              <span className="metric-label">Total Bookings</span>
              <h3 className="metric-value mb-0">{summary.total_count || appointments.length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3} lg={2}>
          <Card className="metric-card metric-confirmed">
            <Card.Body className="p-3">
              <span className="metric-label">Confirmed</span>
              <h3 className="metric-value mb-0 text-success">{summary.confirmed_count || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3} lg={2}>
          <Card className="metric-card metric-pending">
            <Card.Body className="p-3">
              <span className="metric-label">Pending</span>
              <h3 className="metric-value mb-0 text-warning">{summary.pending_count || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3} lg={3}>
          <Card className="metric-card metric-online">
            <Card.Body className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <span className="metric-label">🌐 Online Video</span>
                <h3 className="metric-value mb-0 text-info">{summary.online_count || 0}</h3>
              </div>
              <FaVideo size={24} className="text-info opacity-50" />
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3} lg={3}>
          <Card className="metric-card metric-clinic">
            <Card.Body className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <span className="metric-label">🏥 Clinic Visits</span>
                <h3 className="metric-value mb-0 text-success">{summary.in_person_count || 0}</h3>
              </div>
              <FaClinicMedical size={24} className="text-success opacity-50" />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filter Controls Card */}
      <div className="appointment-controls-card mb-4">
        <Row className="g-3">
          <Col md={4} lg={3}>
            <InputGroup>
              <InputGroup.Text className="bg-transparent border-end-0">
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search patient, phone, doctor, ID..."
                className="border-start-0"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col xs={6} md={2}>
            <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All Modes</option>
              <option value="online">🌐 Online Video</option>
              <option value="in_person">🏥 Physical Clinic</option>
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option value="all">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Payment Pending</option>
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Control
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              title="Filter by appointment date"
            />
          </Col>
          {dateFilter && (
            <Col xs={12} md={1} className="d-flex align-items-center">
              <Button variant="outline-secondary" size="sm" onClick={() => setDateFilter('')}>Clear Date</Button>
            </Col>
          )}
        </Row>
      </div>

      {/* Appointments Table */}
      <div className="appointment-table-card">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading appointments...</p>
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-5">
            <FaCalendarCheck size={48} className="text-muted mb-3 opacity-50" />
            <h5>No appointments found</h5>
            <p className="text-muted">No appointments match your filter criteria.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="appointment-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Patient Details</th>
                  <th>Doctor & Specialty</th>
                  <th>Date & Time</th>
                  <th>Consultation Mode</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Google Meet / Actions</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((appt) => {
                  const isOnline = appt.consultation_type === 'online';
                  return (
                    <tr key={appt.appointment_id}>
                      <td>
                        <strong className="appointment-number">{appt.appointment_number}</strong>
                        <div className="text-muted small">ID: #{appt.appointment_id}</div>
                      </td>
                      <td>
                        <div className="fw-bold">{appt.patient_name}</div>
                        <div className="small text-muted d-flex align-items-center gap-1">
                          <FaPhoneAlt size={10} /> {appt.patient_phone || 'No phone'}
                        </div>
                        {appt.patient_email && (
                          <div className="small text-muted d-flex align-items-center gap-1">
                            <FaEnvelope size={10} /> {appt.patient_email}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold">{appt.doctor_name}</div>
                        <span className="badge appointment-specialty-badge border small">{appt.specialty}</span>
                      </td>
                      <td>
                        <div className="fw-semibold">{formatDate(appt.appointment_date)}</div>
                        <div className="small text-muted d-flex align-items-center gap-1">
                          <FaClock size={10} /> {appt.appointment_time}
                        </div>
                      </td>
                      <td>
                        {isOnline ? (
                          <Badge bg="info" className="d-inline-flex align-items-center gap-1">
                            <FaVideo /> Online Video
                          </Badge>
                        ) : (
                          <Badge bg="secondary" className="d-inline-flex align-items-center gap-1">
                            <FaClinicMedical /> Clinic Visit
                          </Badge>
                        )}
                      </td>
                      <td>
                        {getPaymentBadge(appt.payment_status, appt.payment_method, appt.fee_amount)}
                        {appt.payment_status !== 'paid' && !isOnline && (
                          <div className="mt-1">
                            <Button 
                              variant="outline-success" 
                              size="sm" 
                              className="py-0 px-1 font-xs" 
                              onClick={() => handleQuickMarkPaid(appt)}
                            >
                              Collect Fee
                            </Button>
                          </div>
                        )}
                      </td>
                      <td>{getStatusBadge(appt.status)}</td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          {isOnline && appt.google_meet_link ? (
                            <div className="d-flex align-items-center gap-1">
                              <a
                                href={appt.google_meet_link}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2"
                              >
                                <FaVideo /> Open Meet
                              </a>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="py-1 px-2"
                                title="Copy Meet Link"
                                onClick={() => copyToClipboard(appt.google_meet_link)}
                              >
                                <FaCopy />
                              </Button>
                            </div>
                          ) : isOnline ? (
                            <span className="small text-warning">No Meet link set</span>
                          ) : null}

                          <Button
                            variant="primary"
                            size="sm"
                            className="py-1 px-2 text-nowrap mt-1"
                            onClick={() => handleOpenManage(appt)}
                          >
                            <FaEdit className="me-1" /> Manage
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* Manage Appointment Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered scrollable className="appointment-modal">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaCalendarCheck className="text-primary" />
            Manage Appointment #{selectedAppt?.appointment_number}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveChanges} className="d-flex flex-column flex-grow-1 overflow-hidden" style={{ minHeight: 0, height: "100%" }}>
          <Modal.Body className="p-4 flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
            {selectedAppt && (
              <>
                {/* Patient & Doctor Overview */}
                <div className="overview-box p-3 rounded mb-3">
                  <Row className="g-2">
                    <Col md={6}>
                      <div className="small text-muted">Patient Details:</div>
                      <h6 className="mb-0 fw-bold">{selectedAppt.patient_name}</h6>
                      <div className="small text-muted">{selectedAppt.patient_phone} • {selectedAppt.patient_email}</div>
                    </Col>
                    <Col md={6}>
                      <div className="small text-muted">Doctor & Specialty:</div>
                      <h6 className="mb-0 fw-bold">{selectedAppt.doctor_name}</h6>
                      <div className="small text-muted">{selectedAppt.specialty}</div>
                    </Col>
                  </Row>
                  {selectedAppt.reason && (
                    <div className="mt-2 pt-2 border-top">
                      <span className="small fw-semibold text-muted">Reason for Visit / Symptoms:</span>
                      <p className="mb-0 small appointment-reason-text fst-italic">"{selectedAppt.reason}"</p>
                    </div>
                  )}
                </div>

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Appointment Status</Form.Label>
                      <Form.Select value={modalStatus} onChange={(e) => setModalStatus(e.target.value)}>
                        <option value="pending">⏳ Pending Approval</option>
                        <option value="confirmed">✅ Confirmed</option>
                        <option value="completed">🎉 Completed</option>
                        <option value="cancelled">❌ Cancelled</option>
                        <option value="rescheduled">⏰ Rescheduled</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Consultation Mode</Form.Label>
                      <Form.Control 
                        type="text" 
                        readOnly 
                        disabled 
                        value={selectedAppt.consultation_type === 'online' ? '🌐 Online Video (Google Meet)' : '🏥 Physical Clinic Visit'} 
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Payment Status</Form.Label>
                      <Form.Select value={modalPaymentStatus} onChange={(e) => setModalPaymentStatus(e.target.value)}>
                        <option value="pending">Pending Payment</option>
                        <option value="paid">Paid (Verified)</option>
                        <option value="refunded">Refunded</option>
                        <option value="waived">Waived / Free</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Payment Method</Form.Label>
                      <Form.Select value={modalPaymentMethod} onChange={(e) => setModalPaymentMethod(e.target.value)}>
                        <option value="pay_at_clinic">Pay at Clinic</option>
                        <option value="online">Online (Razorpay / UPI)</option>
                        <option value="cash">Cash (Counter)</option>
                        <option value="card">Card (POS)</option>
                        <option value="upi">UPI QR</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Reschedule Date & Time */}
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Appointment Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={modalDate}
                        onChange={(e) => setModalDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Appointment Time Slot</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. 10:30 AM"
                        value={modalTime}
                        onChange={(e) => setModalTime(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Google Meet Link (Always editable, especially for Online Video) */}
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center gap-1 text-primary">
                    <FaVideo /> Google Meet Link
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="url"
                      placeholder="https://meet.google.com/xyz-abcd-efg"
                      value={modalMeetLink}
                      onChange={(e) => setModalMeetLink(e.target.value)}
                    />
                    {modalMeetLink && (
                      <Button variant="outline-secondary" onClick={() => copyToClipboard(modalMeetLink)}>
                        <FaCopy /> Copy
                      </Button>
                    )}
                  </InputGroup>
                  <Form.Text className="text-muted">
                    This link will be visible to the patient on their {config.appName} mobile app to join the consultation.
                  </Form.Text>
                </Form.Group>

                {/* Admin Internal Notes */}
                <Form.Group className="mb-3">
                  <Form.Label>Internal Admin Notes / Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Add notes about call, reschedule reason, or counter payment..."
                    value={modalAdminNotes}
                    onChange={(e) => setModalAdminNotes(e.target.value)}
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="flex-shrink-0">
            <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save & Update'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AppointmentManagement;
